import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import PropTypes from 'prop-types'
import generateMenuData from 'src/components/layouts/ActualHeader/menuData'
import styles from './index.module.scss'
import NavBar from './NavBar'
import { useAnalytics } from '../../../common/analytics'
import PhoneNumber from '../../shared/PhoneNumber'
import { APPLY_FDR_URL } from '../../../common/constants'
import { useLeadId } from '../../../common/lead-id'
import { buildQueryString } from '../../../common/utils'

import StickyButtons, { LoadTrustpilotItem } from '../StickyButtons'
import useScrollPosition from 'src/common/hooks/useScrollPosition'
import classNames from 'classnames'
import { ConsentBanner } from 'src/components/shared/ConsentBanner'

import { allowedRouteList } from 'src/common/allowed-route-list'
import ABStickyButtons from '../StickyButtons/AB-StickyButtons'
import { ChatbotButtonDesktop } from 'src/components/shared/Chatbot/components/ChatbotButtonDesktop'
import { useChatbot } from 'src/common/ChatbotProvider'
import { useFeatureFlags } from 'src/common/ab-testing/context'
import { featureToggles } from 'src/common/constants/featureToggles'
import { experimentSectionByVariation } from 'src/common/analytics/consts'
import { useAnyElementInViewport } from 'src/common/hooks/useAnyElementInViewport'
import useDeviceDetect from 'src/common/hooks/useDeviceDetect'
import { useBrandedPage } from 'src/hooks/useBrandedPage'
import { useNavigateToApply } from 'src/common/hooks/useNavigateToApply'
import AnimatedArrowRedCTA from 'src/components/shared/CallToActionButton/AnimatedArrowRedCTA'
import ButtonLink from 'src/components/shared/ButtonLink'
import Img from 'src/components/shared/Img'

export const STICKY_HEADER_VIEWPORT_ELEMENT_IDS = [
  'hero-banner-content-cta',
  'heroPrimaryBtnContent',
  'apply-url',
  'title-image',
  'hero-consumer-concept-cta',
  'hero-slider-cta',
  'Continue_Lead_CTA',
  'link-button_PromotingFinancialHealthAndAdvocacy',
  'keyTakeaways',
  'table-of-contents',
  'learn-how-it-works-facts-page-cta',
  'link-heroPrimaryButton',
]

export const Logo = ({
  imageUrl = '/next-assets/icons/fdr-logo.svg',
  width = 100,
  height = 56,
}) => {
  const { track } = useAnalytics()

  const handleLogoClick = useCallback(
    (event) => {
      const data = {
        nav_link_section: 'Header',
        click_type: 'Logo Click',
        click_id: 'Freedom Debt Relief',
        click_url: '/',
        click_text: 'FDR-Web | Freedom Debt Relief',
        track_event: 'global_header',
        event_type: 'track',
      }

      track(data, {}, 'click')
      if (allowedRouteList.includes('/')) {
        event.preventDefault()
        window.location.href = window.location.origin + '/'
      }
    },
    [track]
  )

  return (
    <NextLink
      href="/"
      prefetch={false}
      shallow={true}
      data-testid="logo-link"
      onClick={handleLogoClick}
    >
      <div>
        <Img
          priority={true}
          dataTestId="fdr-logo"
          src={imageUrl}
          alt="Freedom Debt Relief"
          width={width}
          height={height}
          layout="intrinsic"
        />
      </div>
    </NextLink>
  )
}

const Header = ({
  sideBarOpen,
  setSideBarOpen,
  withHeaderTel,
  locale,
  btnClassName,
  statusCode,
  isArticleOrBlogPage = false,
  isBrandPage = false,
  isHome,
  isSalesPage,
  handleSideNavClick,
  showABTestHeader = false,
  flotingPhoneNumber = '800-910-0065',
}) => {
  const { track } = useAnalytics()
  const { navigateToApply } = useNavigateToApply()
  const { isChatbotEnabled } = useChatbot()
  const { isMobile } = useDeviceDetect()
  const leadId = useLeadId()
  const scrollPosition = useScrollPosition()
  const { isBrandedPage } = useBrandedPage()
  const headerRef = useRef()
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (isMobile && headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight)
    }
  }, [isMobile])

  const onViewPort = useAnyElementInViewport(
    STICKY_HEADER_VIEWPORT_ELEMENT_IDS,
    headerHeight
  )

  const { query: baseQuery } = useRouter()
  const { getExperimentVariation } = useFeatureFlags()

  let query = useMemo(
    () => buildQueryString(baseQuery, leadId),
    [baseQuery, leadId]
  )
  const headerBannerText = sideBarOpen ? 'hidden' : 'hidden md:block lg:p-1'

  const chatbotExperiment = getExperimentVariation(
    experimentSectionByVariation[featureToggles.FDR_CHATBOT]
  )

  const handleButtonClick = useCallback(
    //todo changed as CTA button, but destination url is https://fdr-dev.programdashboard.com/login
    (href, name, section, btn = true, callUs = false) => {
      const data = {
        nav_link_section: section,
        click_type: btn ? 'Button Click' : 'Phone Click',
        click_id: name,
        click_url: href,
        click_text: `FDR-Web | ${name}`,
        track_event: 'global_header',
      }
      if (callUs) {
        track(
          data,
          {
            experimentSection: 'clientLogin',
            event_type: 'start',
            variation: 'control',
          },
          'click',
          statusCode
        )
      } else {
        track(data, {}, 'click', statusCode)
      }
    },
    [track, statusCode]
  )

  const handleSeeIfYouQualifyDinamycHeaderClick = (href) => {
    const data = {
      click_id: 'See if you qualify',
      click_text: 'FDR-Web | See if you qualify',
      click_type: 'Button Click',
      click_url: href,
      nav_link_section: 'Persistent Header',
      track_event: 'button_click',
    }
    track(data, {}, 'button_click')
  }

  const handleCallUsDinamycHeaderClick = (href) => {
    const data = {
      click_id: 'Call Us',
      click_text: 'FDR-Web | Call Us',
      click_type: 'Phone Click',
      click_url: href,
      nav_link_section: 'Persistent Header',
      track_event: 'global_header',
    }
    track(data, {}, 'click')
  }

  const showStickyHeader = !onViewPort && scrollPosition > 150
  const showTrustpilot = !isBrandedPage && showStickyHeader

  return (
    <>
      <div className="mb-0 lg:mb-24" />
      <header
        ref={headerRef}
        className={classNames(
          'fixed top-0 z-100 flex w-full justify-start bg-white shadow-header duration-300 lg:static lg:justify-center lg:shadow-none'
        )}
        data-testid="app-header"
        id="app-header"
      >
        {/* Desktop */}
        <div className="gap-x-primary col-span-full hidden grid-cols-4 py-2 text-ms text-gray-510 lg:grid lg:grid-cols-12 lg:py-0">
          <div className="col-span-full col-start-1 m-auto lg:col-span-12">
            <div className={`${styles.wrapper} flex`}>
              <Logo imageUrl="/next-assets/icons/fdr-logo.svg" />
              <NavBar
                menuData={generateMenuData(false)}
                sideBarOpen={sideBarOpen}
                setSideBarOpen={setSideBarOpen}
                locale={locale}
                btnClassName={btnClassName}
                statusCode={statusCode}
                showButtonOptions
              />
            </div>
          </div>
          <div
            className={`col-span-3 hidden pr-2 lg:order-first lg:col-span-12 lg:block`}
          >
            {withHeaderTel && (
              <div
                className={headerBannerText}
                data-testid="header-banner-text"
              >
                <div className="flex h-[21px] items-center justify-end">
                  <span className="text-xs text-black-light">
                    Talk to a certified debt consultant
                  </span>
                  <PhoneNumber
                    href={`tel:${flotingPhoneNumber}`}
                    className="ml-2 text-base font-semibold text-black-light no-underline"
                    text={flotingPhoneNumber}
                    isTrack={true}
                    statusCode={statusCode}
                    nav_link_section="Above header"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="fixed bottom-0 left-0 flex w-full flex-col justify-center">
            {isChatbotEnabled &&
              showStickyHeader &&
              ['Variation_A', 'Variation_B'].includes(chatbotExperiment) && (
                <div className="w-content z-50 mx-auto mb-8 flex w-full justify-end">
                  <ChatbotButtonDesktop className="mr-5" />
                </div>
              )}
            <ConsentBanner />
          </div>
        </div>

        {/* Mobile */}
        <div className={classNames('block w-full shadow-header lg:hidden')}>
          <div className="mx-3 flex h-18 flex-equal items-center gap-2 py-2">
            <div>
              <NavBar
                menuData={generateMenuData(true)}
                sideBarOpen={sideBarOpen}
                setSideBarOpen={setSideBarOpen}
                locale={locale}
                btnClassName={btnClassName}
                statusCode={statusCode}
                showButtonOptions
              />
              <button
                type="button"
                onClick={handleSideNavClick}
                className="focus:outline-none"
                aria-label="menu toggle"
              >
                <svg className="size-6 fill-current" viewBox="0 0 24 24">
                  {/* Show X button */}
                  {sideBarOpen ? (
                    <path
                      fillRule="evenodd"
                      d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"
                    />
                  ) : (
                    <path
                      fillRule="evenodd"
                      d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"
                    />
                  )}
                </svg>
              </button>
            </div>

            {showStickyHeader ? (
              <div
                className="flex grow items-center justify-between gap-3"
                data-testid="mobile-header-sticky"
              >
                <ButtonLink
                  className="w-full whitespace-nowrap rounded-lg border-2 border-blue-base bg-white px-4 py-2.5 text-16 font-bold text-blue-base"
                  href={`tel:${flotingPhoneNumber}`}
                  onClick={handleCallUsDinamycHeaderClick}
                >
                  Call us
                </ButtonLink>
                <AnimatedArrowRedCTA
                  buttonText="See if you qualify"
                  url={`${APPLY_FDR_URL()}?${query}`}
                  onClick={(e) => {
                    e.preventDefault()
                    handleSeeIfYouQualifyDinamycHeaderClick(
                      `${APPLY_FDR_URL()}?${query}`
                    )
                    setTimeout(() => navigateToApply(), 800)
                  }}
                  className="w-full min-w-[205px] px-6 py-[15px]"
                  textClassName="text-white font-bold text-[16px] leading-none"
                  withArrow={false}
                />
              </div>
            ) : (
              <div
                className="flex grow items-center justify-between gap-2"
                data-testid="mobile-header-default"
              >
                <Logo
                  imageUrl="/next-assets/icons/fdr-logo.svg"
                  width={74}
                  height={56}
                />
                <div className="flex items-center justify-between gap-1">
                  <ButtonLink
                    href={`tel:${flotingPhoneNumber}`}
                    className="z-10 mr-3 whitespace-nowrap rounded-lg bg-blue-540 px-2.5 py-1.5 text-13 font-bold leading-22 text-blue-base"
                    onClick={(href) =>
                      handleButtonClick(
                        href,
                        'Call Us',
                        'Mobile header',
                        false,
                        true
                      )
                    }
                  >
                    Call Us
                  </ButtonLink>
                  <ButtonLink
                    href={`${APPLY_FDR_URL()}?${query}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`whitespace-nowrap rounded-lg border-2 border-blue-base bg-white px-2.5 py-1 text-13 font-bold text-blue-base lg:ml-4 ${btnClassName}`}
                    onClick={(href) =>
                      handleButtonClick(href, 'See if you qualify', 'Header')
                    }
                  >
                    See if you qualify
                  </ButtonLink>
                </div>
              </div>
            )}
          </div>
          {!isBrandedPage && (
            <div
              className={classNames(
                'w-full shrink-0 overflow-hidden',
                showStickyHeader ? 'h-8' : 'h-0'
              )}
              aria-hidden={!showTrustpilot}
              data-testid="mobile-header-trustpilot"
            >
              <div
                className={classNames(
                  'size-full transition-opacity duration-200',
                  showTrustpilot ? 'visible opacity-100' : 'invisible opacity-0'
                )}
              >
                <LoadTrustpilotItem />
              </div>
            </div>
          )}
        </div>
      </header>
      {isSalesPage ? null : (
        <StickyButtons
          handleButtonClick={handleButtonClick}
          phoneNumberText={flotingPhoneNumber}
          show={showStickyHeader}
        />
      )}
      {showABTestHeader && (
        <ABStickyButtons
          handleButtonClick={handleButtonClick}
          phoneNumberText={flotingPhoneNumber}
        />
      )}
    </>
  )
}

Header.defaultProps = {
  sideBarOpen: false,
  withHeaderTel: true,
  locale: 'en',
  btnClassName: undefined,
  isHome: false,
}

Header.propTypes = {
  sideBarOpen: PropTypes.bool,
  withHeaderTel: PropTypes.bool,
  locale: PropTypes.string,
  setSideBarOpen: PropTypes.func,
  btnClassName: PropTypes.string,
  statusCode: PropTypes.number,
  isHome: PropTypes.bool,
  isSalesPage: PropTypes.bool,
}

export default Header
